import styled from 'styled-components';
import { Alert, Button, Modal, Steps, Form, Radio } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  HomeOutlined,
  BuildOutlined,
  PlusOutlined,
  LinkOutlined
} from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import RecordSelect from "../fields/RecordSelect";

import {
  UnitTypeModel,
  UnitModel,
  IUnitType,
  IUnit,
  ListingModel, OwnerModel
} from "../../types/models/housing";
import RecordTable from "../RecordTable";
import { IUnitCapex, IUnitOpex, UnitCapexModel, UnitOpexModel } from "../../types/models/economics";
import { ancanaApi } from "../../api/ancanaApi";
import RecordGalleries from "../RecordGalleries";
import { reject } from "ramda";
import { nestedRecordToFormData } from "../RecordDrawer";
import RecordFieldsMapper from "../RecordFieldsMapper";
import {
  ISalesMaterialConfig,
  SalesMaterialConfigModel
} from "../../types/models";
import EmbeddedRecordViewer from "../EmbeddedRecordViewer";
import SalesMaterialsPanel from "./presentations/SalesMaterialsPanel";
import PaymentPlansPanel from "./paymentPlans/PaymentPlansPanel";
import useTheme from "../../hooks/useTheme";
import { BrowserRouter } from "react-router-dom";

// const fs = require('fs');

const Container = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 200px);

  .steps-content {
    display: flex;
    flex-direction: column;
    margin: auto;
    margin-top: 16px;
    padding-left: 9.5%;
    padding-right: 9.5%;
    width: 100%;
    height: calc(100% - 24px);
    align-content: center;
    overflow-y: auto;
    // justify-content: center;
  }
  label {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  .uploader {
    width: 100%;
  }

  .ant-upload-list {

  }

  .ant-radio {
    margin-left: 12px;
    align-self: flex-start;
    margin-top: 10px;
  }

  .radio-box {
    border: 1px ${({ theme }) => theme === 'dark' ? "rgba(255,255,255,.12)" : "rgba(0,0,0,.12)"} solid;
    margin-left: -36px;
    margin-right: 0;
    padding: 12px 16px;
    text-align: center;
    width: 210px;
    height: 172px;
  }

  .ant-divider-horizontal.ant-divider-with-text::before, .ant-divider-horizontal.ant-divider-with-text::after {
    top: 0;
  }
`;

interface ListingWizardProps {
  createRecord: any;
  recordFields: any;
  fetchRecordLocalizedTexts: any;
  onSuccess?: any;
  record?: any | null;
  setRecord?: any;
  patchRecord?: any;
  onClose?: any;
  contentType?: any;
}

const STEP_COUNT = 5;

function ListingWizard(props: ListingWizardProps) {
  const { onSuccess, createRecord, record, setRecord, onClose, recordFields, fetchRecordLocalizedTexts } = props;
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formValsCopy, setFormValsCopy] = useState<any>({});
  const [relatedRecordFilter, setRelatedRecordFilter] = useState<any>({});
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const unitsTableRef = useRef<any>();
  const { currentTheme } = useTheme();

  const isEditMode = !!record;
  // const enDescriptionFieldId = "field_chat_geo_location_1_description__en";

  const subjectId = !!formValsCopy ? (
    formValsCopy['type'] === 'unit' ? formValsCopy['unit'] : formValsCopy['unit_type']
  ) : undefined;

  const reset = () => {
    setCurrent(0);
    setFormValsCopy(undefined);
    form.resetFields();
  }

  const isNextDisabled = () => {
    if (formValsCopy === undefined){
      return true;
    }
    if (current === 0) {
      return !(formValsCopy['type'] && subjectId);
    }
    if (current === 1) {
      // return !(formValsCopy['unit']);
      return false;
    }
  }

  const handleFinish = async (data: any) => {
    setSaving(true);
    let res;
    if (formValsCopy['type'] === 'unit') {
      res = await ancanaApi.get(`housing-units/${data['unit']}/`);
    } else {
      res = await ancanaApi.get(`housing-designs/${data['unit_type']}/`);
    }
    const payload = {
      name: res.data.name,
      slug: res.data.name
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, ''),
      /*
      localized_data: {
        descriptions: {
          es: data.description_es || "",
          en: data.description_en || "",
        }
      },*/
      design: formValsCopy['type'] === 'unit' ? data.design : subjectId,
      ...data,
    };

    if(!!record) {
      // omit(difference(Object.keys(record), Object.keys(data)), record)
      props.patchRecord(record.slug, reject((val) => val === null, data));
      setCurrent(0);
      setRecord(null);
    } else {
      createRecord(payload, reset);
      setOpen(false);
    }
    setSaving(false);
    if(onClose) {
      onClose();
    }
  }

  useEffect(() => {
    if (formValsCopy !== undefined && formValsCopy['type'] === 'unit' && !!subjectId) {
      setRelatedRecordFilter({ unit: formValsCopy['unit'] });
    } else if (formValsCopy !== undefined && formValsCopy['type'] === 'unit_type' && !!subjectId) {
      setRelatedRecordFilter({ unit_type: subjectId, design: subjectId });
    } else {
      setRelatedRecordFilter({});
    }
  }, [subjectId]);

  useEffect(() => {
    if (!!record) {
      setOpen(true);
      let formattedInitValues = nestedRecordToFormData(record);
      formattedInitValues = { ...formattedInitValues, unit_type: formattedInitValues.design };
      form.setFieldsValue(formattedInitValues);
      setFormValsCopy(formattedInitValues);

      // Get all language texts for edit
      if (Object.keys(recordFields || {}).some(recordKey => recordKey.includes('__'))) {
        const localizedTexts: any = {};
        Object.keys(recordFields || {}).filter(recordKey => recordKey.includes('__')).forEach(locKey => {
          localizedTexts[locKey] = null;
        });
        fetchRecordLocalizedTexts(Number(record.id)).then((texts: any[]) => {
          texts.forEach((lt: any) => localizedTexts[`${lt.field_name}__${lt.locale}`] = lt.text);
          form.setFieldsValue(localizedTexts);
        });
      }
    } else {
      setOpen(false);
    }
  }, [record]);

  return (
    <>
      {record === undefined && (
      <Button type="primary" onClick={() => setOpen(true)} icon={<PlusOutlined />}>
        Create Listing
      </Button>
      )}
      <Modal
        title={`${!!record ? 'Edit' : 'Create'} Listing${!!record && `: ${record.name}`}`}
        centered
        visible={open}
        width="900px"
        onCancel={() => {
          if (!!record) {
            setRecord(null);
          } else {
            setOpen(false);
            setCurrent(0);
          }
          if(onClose) {
            onClose();
          }
        }}
        footer={
          <div className="full-width justify-between" style={{ display: 'flex' }}>
            <Button
              type="primary"
              ghost
              disabled={current === 0}
              onClick={() => setCurrent(current - 1)}
            >
              <LeftOutlined /> Back
            </Button>
            {current < (STEP_COUNT-1) && (
              <Button
                type="primary"
                onClick={() => {
                  setCurrent(current + 1);
                }}
                disabled={isNextDisabled()}
              >
                Next <RightOutlined />
              </Button>
            )}
            {current === (STEP_COUNT-1) && (
              <Button type="primary" onClick={() => form.submit()} loading={saving}>
                {!!record ? 'Save Changes' : 'Create Listing'}
              </Button>
            )}
          </div>
        }
      >
        <Container theme={currentTheme}>
          <Steps current={current}>
            <Steps.Step title="Type" />
            <Steps.Step title="Details" />
            <Steps.Step title={(!!formValsCopy['type'] && formValsCopy['type'] === 'unit_type') ? 'Units' : 'Costs'} />
            <Steps.Step title="Images" />
            <Steps.Step title="Sales Materials" />
          </Steps>

          <Form
            form={form}
            onValuesChange={(newData) => setFormValsCopy({ ...formValsCopy, ...newData })}
            onFinish={handleFinish}
            layout="vertical"
            style={{ height: '100%', width: '100%', position: 'absolute' }}
          >
            <div className="steps-content" style={{ visibility: current === 0 ? 'visible' : 'hidden', position: 'absolute' }}>
              <div className="fs-3-5 fw-500 mt-lg">
                Select type
              </div>
              <p>
                Is the listing gonna be for a unit or a unit type?
              </p>
              <Form.Item name="type">
                <Radio.Group style={{ display: 'flex', width: '100%' }}>
                  <Radio value="unit" style={{ flex: 1 }}>
                    <div className="radio-box" style={{ width: '100%' }}>
                      <HomeOutlined style={{ fontSize: 24, opacity: 0.4, marginBottom: 8 }} />
                      <div className="fs-3-5 mb-sm">Unit</div>
                      <div className="fs-2-33 fw-400">
                        The listing will show information of a single unit
                      </div>
                    </div>
                  </Radio>
                  <Radio value="unit_type" style={{ flex: 1 }}>
                    <div className="radio-box" style={{ width: '100%' }}>
                      <BuildOutlined style={{ fontSize: 24, opacity: 0.4, marginBottom: 8 }} />
                      <div className="fs-3-5 mb-sm">Unit Type</div>
                      <div className="fs-2-33 fw-400">
                        A listing for multiple units with shared characteristics
                      </div>
                    </div>
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {!!formValsCopy && !!formValsCopy['type'] && formValsCopy['type'] === 'unit' && (
                <RecordSelect<IUnit>
                  recordModel={UnitModel}
                  allowDrawerCreate
                  allowManage
                  onCreate={data => {
                    form.setFieldsValue({ unit: data.id.toString() });
                    setFormValsCopy({ ...formValsCopy, unit: data.id.toString() });
                  }}
                  highlightRecords={formValsCopy['unit'] ? [Number(formValsCopy['unit'])] : undefined}
                  visibleFields={['design', 'name', 'fractions', 'weeks_available', 'address', 'zip_code', 'lat', 'lng']}
                />
              )}

              {!!formValsCopy && !!formValsCopy['type'] && formValsCopy['type'] === 'unit_type' && (
                <RecordSelect<IUnitType>
                  formName="unit_type"
                  label="Unit Type"
                  recordModel={UnitTypeModel}
                  allowDrawerCreate
                  allowManage
                  onCreate={data => {
                    form.setFieldsValue({ unit_type: data.id.toString() });
                    setFormValsCopy({ ...formValsCopy, unit_type: data.id.toString() });
                  }}
                  highlightRecords={formValsCopy['unit_type'] ? [Number(formValsCopy['unit_type'])] : undefined}
                />
              )}
            </div>

            <div className="steps-content" style={{ visibility: current === 1 ? 'visible' : 'hidden', position: 'absolute' }}>
              <div className="mt-lg fw-500 fs-3-5">
                Listing details
              </div>
              <p>
                Latitude and longitude can be taken from google maps and must have 6 decimal digits.
              </p>

              <RecordFieldsMapper
                formRef={{ current: form }}
                fieldDefinitions={recordFields}
                recordModel={ListingModel}
                contentType={props.contentType}
                recordInstance={record}
                omit={['name', 'type', 'published', 'sort_index', 'amenities', 'unit', 'design', 'description_en', 'description_es']}
              />
            </div>

            <div className="steps-content" style={{ visibility: current === 2 ? 'visible' : 'hidden', position: 'absolute' }}>
              {!!formValsCopy && formValsCopy['type'] === 'unit' && !!subjectId && (
                <RecordTable<IUnitCapex>
                  className="mt-lg"
                  recordModel={UnitCapexModel}
                  recordsetFilters={relatedRecordFilter}
                  customRecordName="Pricing Details"
                  footer={(records) => (
                    <div className="fs-2-66">
                      Final Price <span className="fw-600">${records.reduce((a, c) => Number(c.amount) + a, 0).toLocaleString()}</span>
                    </div>
                  )}
                  noPagination
                />
              )}
              <br /><br />
              {!!formValsCopy && formValsCopy['type'] === 'unit' && !!subjectId && (
                <RecordTable<IUnitOpex>
                  recordModel={UnitOpexModel}
                  recordsetFilters={relatedRecordFilter}
                  customRecordName="Expenses"
                  footer={(records) => {
                    // Group records by periodicity
                    const groupedByPeriodicity = records.reduce((acc, record) => {
                      const periodicity = record.periodicity || 'unknown';
                      if (!acc[periodicity]) {
                        acc[periodicity] = [];
                      }
                      acc[periodicity].push(record);
                      return acc;
                    }, {} as Record<string, IUnitOpex[]>);

                    // Calculate totals for each periodicity
                    const periodicityTotals = Object.entries(groupedByPeriodicity).map(([periodicity, periodicityRecords]) => {
                      const total = periodicityRecords.reduce((sum, record) => sum + Number(record.amount), 0);
                      return { periodicity, total };
                    });

                    // Calculate grand total
                    const grandTotal = records.reduce((a, c) => Number(c.amount) + a, 0);

                    // Capitalize periodicity for display
                    const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

                    return (
                      <div className="fs-2-66">
                        {periodicityTotals.map(({ periodicity, total }) => (
                          <div key={periodicity} style={{ marginBottom: '4px' }}>
                            {capitalizeFirst(periodicity)} Expenses: <span className="fw-600">${total.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                  noPagination
                />
              )}
              <br /><br />
              {!!formValsCopy && formValsCopy['type'] === 'unit' && !!subjectId && (
                <PaymentPlansPanel unitId={subjectId} />
              )}
              {!!formValsCopy && formValsCopy['type'] === 'unit_type' && !!subjectId && (
                <RecordTable<IUnit>
                  ref={unitsTableRef}
                  recordModel={UnitModel}
                  recordsetFilters={relatedRecordFilter}
                  embedded
                  actionsWhitelist={['edit', 'gallery']}
                  fieldWhitelist={['name', 'price', 'square_meters', 'bedrooms', 'bathrooms']}
                  headerExtra={(
                    <Button
                      type="link"
                      onClick={() => {
                        Modal.confirm({
                          title: `Link existing unit to this unit type`,
                          content: (
                            <BrowserRouter>
                              <Form
                                className="pt-md"
                                form={form2}
                                name="basic"
                                labelCol={{ span: 8 }}
                                wrapperCol={{ span: 16 }}
                                initialValues={{ remember: true }}
                                onFinish={async (data) => {
                                  await ancanaApi.patch(`housing-units/${data.unit}/`, { design: subjectId });
                                  unitsTableRef.current?.reload();
                                  form2.resetFields();
                                  Modal.destroyAll();
                                }}
                                onFinishFailed={() => {}}
                                autoComplete="off"
                              >
                                <RecordSelect
                                  formName="unit"
                                  recordModel={UnitModel}
                                />
                              </Form>
                            </BrowserRouter>
                          ),
                          okText: "Link",
                          icon: null,
                          onOk: (close: any) => form2.submit()
                        });
                      }}
                    >
                      <LinkOutlined /> Link existing Unit
                    </Button>
                  )}
                />
              )}
              <br /><br /><br/>
            </div>
          </Form>

          {current === 3 && (
            <div className="steps-content" style={{ position: 'absolute' }}>
              <div className="mt-lg fw-500 fs-3-5 capitalize">
                {formValsCopy['type']} images
              </div>
              <p>
                Each image gallery is a tab, these galleries are for grouping images (for example: Kitchen, Front)
              </p>
              <RecordGalleries recordId={subjectId} recordName="House" recordPath={`housing-${(!!formValsCopy && formValsCopy['type'] === 'unit') ? 'units' : 'designs'}`} />
            </div>
          )}

          {current === 4 && (
            <div className="steps-content" style={{ position: 'absolute' }}>
              <div className="mt-lg fw-500 fs-3-5">
                Sales Materials
              </div>
              <p>
                Brochures and presentations built from this listing’s data.
              </p>
              <br />

              {!isEditMode ? (
                <Alert
                  type="info"
                  showIcon
                  message="Available once the listing is created"
                  description={
                    "Nothing needs configuring here — sales materials read this " +
                    "listing’s data directly. Create the listing, then come back " +
                    "to generate them."
                  }
                />
              ) : (
                <SalesMaterialsPanel
                  listingId={formValsCopy['id'] as number}
                  compact
                  onEditDetails={() => setCurrent(1)}
                />
              )}
              <br />
              <br />
            </div>
          )}
        </Container>
      </Modal>
    </>
  );
}

export default ListingWizard;
